import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Upload,
    FileDown,
    AlertTriangle,
    CheckCircle,
    XCircle,
    FileSpreadsheet,
    Loader2,
    Calendar,
    Lock,
    History,
    Download,
    RefreshCw,
    Info,
    ShieldCheck,
    Users
} from 'lucide-react';
import { BASE_URL, apiClient } from '../utils/apiClient';

export default function LeaveBalanceImportPage() {
    const todayStr = new Date().toISOString().slice(0, 10);

    const [file, setFile] = useState(null);
    const [excelBaseDate, setExcelBaseDate] = useState(todayStr);
    const [syncAutoAnnualStartDate, setSyncAutoAnnualStartDate] = useState(true);
    const [importMode, setImportMode] = useState('Equalize'); // 'Equalize' | 'Add'

    const [uploading, setUploading] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(null);

    const [settings, setSettings] = useState(null);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const pollRef = useRef(null);

    const authHeaders = () => {
        const token = localStorage.getItem('authToken');
        const firmCode = localStorage.getItem('firmCode') || sessionStorage.getItem('firmCode') || '';
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (firmCode) headers['X-Firm-Code'] = firmCode;
        return headers;
    };

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const loadSettingsAndHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const [settingsRes, historyRes] = await Promise.all([
                apiClient('leave-balances/settings').catch(() => null),
                apiClient('leave-balances/import/history').catch(() => [])
            ]);
            if (settingsRes) setSettings(settingsRes);
            if (Array.isArray(historyRes)) setHistory(historyRes);
        } catch (err) {
            console.error('Ayarlar veya aktarım geçmişi alınamadı:', err);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const applyStatus = useCallback((data) => {
        setProgress(data);
        if (!data?.isRunning) {
            stopPolling();
            setUploading(false);
            if (data?.status === 'Completed' || data?.status === 'Failed') {
                setResult({
                    successCount: data.successCount ?? 0,
                    skippedZeroDeltaCount: data.skippedZeroDeltaCount ?? 0,
                    failedCount: data.failedCount ?? 0,
                    totalRows: data.totalRows ?? 0,
                    message: data.message,
                    errors: data.errors || []
                });
                if (data.status === 'Completed' && (data.failedCount ?? 0) === 0) {
                    setFile(null);
                }
                if (data.status === 'Failed' && data.message) {
                    setError(data.message);
                }
                loadSettingsAndHistory();
            }
        }
    }, [loadSettingsAndHistory]);

    const startPolling = useCallback((runId) => {
        stopPolling();
        const tick = async () => {
            try {
                const res = await fetch(`${BASE_URL}/leave-balances/import/status/${runId}`, {
                    headers: authHeaders()
                });
                if (!res.ok) return;
                const data = await res.json();
                applyStatus(data);
            } catch (e) {
                console.error(e);
            }
        };
        tick();
        pollRef.current = setInterval(tick, 1000);
    }, [applyStatus]);

    useEffect(() => {
        loadSettingsAndHistory();

        // Sayfa açılınca bu firmada çalışan aktif bir izin bakiye aktarımı varsa kilitle ve progress bar'ı göster
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}/leave-balances/import/active`, {
                    headers: authHeaders()
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data?.isRunning && data.runId) {
                    setUploading(true);
                    applyStatus(data);
                    startPolling(data.runId);
                }
            } catch {
                /* ignore */
            }
        })();

        return () => stopPolling();
    }, [loadSettingsAndHistory, applyStatus, startPolling]);

    const handleDownloadTemplate = async (prefillEmployees) => {
        try {
            setDownloadingTemplate(true);
            setError(null);
            const res = await fetch(`${BASE_URL}/leave-balances/import/template?prefillEmployees=${prefillEmployees}`, {
                headers: authHeaders()
            });
            if (!res.ok) {
                const errText = await res.text();
                throw new Error(errText || 'Şablon indirilemedi.');
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = prefillEmployees
                ? `Aktif_Personel_Izin_Bakiye_Sablonu_${todayStr}.xlsx`
                : 'Ornek_Izin_Bakiye_Aktarim_Sablonu.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Şablon indirilirken bir hata oluştu.');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const handleDownloadArchivedFile = async (storedFileName) => {
        try {
            const res = await fetch(`${BASE_URL}/leave-balances/import/history/${encodeURIComponent(storedFileName)}/download`, {
                headers: authHeaders()
            });
            if (!res.ok) {
                throw new Error('Arşiv dosyası indirilemedi.');
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = storedFileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Dosya indirilirken hata oluştu.');
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setResult(null);
            setError(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Lütfen yüklenecek Excel (.xlsx) dosyasını seçiniz.');
            return;
        }
        if (!excelBaseDate) {
            setError('Lütfen Excel tablosunun hazırlandığı baz tarihi (kesim tarihini) seçiniz.');
            return;
        }

        setUploading(true);
        setError(null);
        setResult(null);
        setProgress({
            isRunning: true,
            status: 'Queued',
            progressPercent: 0,
            processedRows: 0,
            totalRows: 0,
            successCount: 0,
            failedCount: 0,
            message: 'Excel dosyası sunucuya yükleniyor ve arşivleniyor…'
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('excelBaseDate', excelBaseDate);
        formData.append('syncAutoAnnualStartDate', syncAutoAnnualStartDate ? 'true' : 'false');
        formData.append('importMode', importMode);

        try {
            const response = await fetch(`${BASE_URL}/leave-balances/import/excel`, {
                method: 'POST',
                headers: authHeaders(),
                body: formData
            });

            const data = await response.json().catch(() => null);

            if (response.status === 409 && data?.runId) {
                setError(data.message || 'Bu firma için şu anda devam eden başka bir izin bakiye aktarımı var.');
                applyStatus(data.status || { isRunning: true, runId: data.runId });
                startPolling(data.runId);
                return;
            }

            if (!response.ok) {
                throw new Error(data?.message || 'Aktarım başlatılırken bir hata oluştu.');
            }

            if (data?.runId) {
                applyStatus(data.status || {
                    isRunning: true,
                    runId: data.runId,
                    status: 'Queued',
                    progressPercent: 2,
                    message: 'Kuyruğa alındı…'
                });
                startPolling(data.runId);
            } else {
                setUploading(false);
            }
        } catch (err) {
            setError(err.message || 'Yükleme başarısız oldu.');
            setUploading(false);
            setProgress(null);
        }
    };

    const formatDateTr = (val) => {
        if (!val) return '-';
        try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleDateString('tr-TR');
        } catch {
            return String(val);
        }
    };

    const formatDateTimeTr = (val) => {
        if (!val) return '-';
        try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return String(val);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes <= 0) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const currentAutoStartDateStr = settings?.yillikIzinOtomatikBaslangicTarihi
        ? String(settings.yillikIzinOtomatikBaslangicTarihi).slice(0, 10)
        : null;
    const lastExcelBaseDateStr = settings?.sonExcelBazTarihi
        ? String(settings.sonExcelBazTarihi).slice(0, 10)
        : null;
    const hasDateMismatch =
        currentAutoStartDateStr &&
        lastExcelBaseDateStr &&
        currentAutoStartDateStr !== lastExcelBaseDateStr;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            {/* Üst Başlık */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                    <div className="p-3 bg-blue-600 text-white rounded-xl shadow-md">
                        <FileSpreadsheet className="w-7 h-7" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            İzin Bakiye Aktarımı (Excel)
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Eski PDKS veya İK sisteminizdeki personel yıllık izin devir bakiyelerini toplu olarak aktarın ve arşivleyin.
                        </p>
                    </div>
                </div>

                {settings?.sonExcelYuklemeTarihi && (
                    <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-2.5 shadow-sm flex items-center gap-3">
                        <History className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <div className="text-xs">
                            <div className="text-gray-500 dark:text-gray-400 font-medium">
                                Son Excel Yükleme Tarihi
                            </div>
                            <div className="font-bold text-gray-900 dark:text-white">
                                {formatDateTimeTr(settings.sonExcelYuklemeTarihi)}{' '}
                                <span className="font-normal text-gray-500">
                                    ({settings.sonExcelYukleyenKullanici || 'Sistem'})
                                </span>
                            </div>
                            <div className="text-blue-600 dark:text-blue-400 font-semibold mt-0.5">
                                Excel Baz Tarihi: {formatDateTr(settings.sonExcelBazTarihi)}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* KRİTİK UYARI KARTI: Excel Baz Tarihi & Yıllık İzin Hakediş Başlangıç Tarihi Uyumu */}
            <div className={`rounded-2xl border p-5 shadow-sm ${
                hasDateMismatch
                    ? 'bg-red-50/90 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                    : 'bg-amber-50/90 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
            }`}>
                <div className="flex items-start gap-3.5">
                    <AlertTriangle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
                        hasDateMismatch ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
                    }`} />
                    <div className="space-y-2 text-sm">
                        <h3 className={`font-bold text-base ${
                            hasDateMismatch ? 'text-red-900 dark:text-red-200' : 'text-amber-900 dark:text-amber-200'
                        }`}>
                            ⚠️ Çok Önemli: Yıllık İzin Hakediş Başlangıç Tarihini Excel Tablonuzun Hazırlandığı Tarihe Göre Ayarlayın!
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            Excel tablosunu eski sisteminizden <strong>10 gün önce</strong> hazırlamış olabilirsiniz veya <strong>bugün</strong> çekmiş olabilirsiniz.
                            Otomatik Yıllık İzin Hakediş sisteminin mükerrer (çift) izin tanımlamaması veya bu ara dönemdeki yıl dönümlerini atlamaması için,
                            <strong> Otomatik Yıllık İzin Hakediş Başlangıç Tarihi</strong> kesinlikle <strong>Excel tablosunu güncellediğiniz/çektiğiniz tarihe (Baz Tarihe)</strong> ayarlanmalıdır.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 pt-1">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-800 dark:text-gray-200">
                                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                Mevcut Otomatik Hakediş Başlangıç Tarihi:{' '}
                                <strong className="text-blue-600 dark:text-blue-400">
                                    {settings?.yillikIzinOtomatikBaslangicTarihi
                                        ? formatDateTr(settings.yillikIzinOtomatikBaslangicTarihi)
                                        : 'Henüz Ayarlanmadı'}
                                </strong>
                            </span>

                            {settings?.sonExcelBazTarihi && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-800 dark:text-gray-200">
                                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                                    Son Yüklenen Excel Baz Tarihi:{' '}
                                    <strong className="text-emerald-600 dark:text-emerald-400">
                                        {formatDateTr(settings.sonExcelBazTarihi)}
                                    </strong>
                                </span>
                            )}

                            {hasDateMismatch && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-600 text-white text-xs font-bold">
                                    Dikkat: Son Excel Baz Tarihi ile Otomatik Hakediş Başlangıç Tarihi farklı!
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 1. ADIM: Şablon İndirme Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                            1. Adım • Excel Hazırlığı
                        </span>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
                            Örnek Excel Formatını İndirin ve Doldurun
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            İster boş şablonu indirip eski sisteminizden kopyala-yapıştır yapabilir, isterseniz sistemdeki <strong>aktif personellerinizin TCKN ve Sicil No bilgileri hazır doldurulmuş</strong> Excel tablosunu indirip sadece <em>Kalan İzin (Gün)</em> sütununu doldurabilirsiniz.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3 flex-shrink-0">
                        <button
                            type="button"
                            disabled={downloadingTemplate}
                            onClick={() => handleDownloadTemplate(false)}
                            className="inline-flex items-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                        >
                            <FileDown className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                            Boş Örnek Şablon İndir (.xlsx)
                        </button>

                        <button
                            type="button"
                            disabled={downloadingTemplate}
                            onClick={() => handleDownloadTemplate(true)}
                            className="inline-flex items-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors disabled:opacity-50"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Aktif Personellerle Dolu Şablon İndir (.xlsx)
                        </button>
                    </div>
                </div>

                {/* Sütun Rehberi */}
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-xs text-left border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 uppercase">
                            <tr>
                                <th className="py-2 px-3">Excel Sütunu</th>
                                <th className="py-2 px-3">Zorunluluk</th>
                                <th className="py-2 px-3">Örnek Değer</th>
                                <th className="py-2 px-3">Açıklama</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-600 dark:text-gray-300">
                            <tr>
                                <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">1. TCKN / 2. Sicil No</td>
                                <td className="py-2 px-3"><span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">İkisinden Biri Zorunlu</span></td>
                                <td className="py-2 px-3 font-mono">11111111111 veya PRS-0001</td>
                                <td className="py-2 px-3">Sistem önce TCKN, boşsa Sicil No ile personeli eşleştirir.</td>
                            </tr>
                            <tr>
                                <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">3. Ad Soyad / 4. Departman</td>
                                <td className="py-2 px-3"><span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Bilgi Amaçlı</span></td>
                                <td className="py-2 px-3">Ahmet Yılmaz / Üretim</td>
                                <td className="py-2 px-3">Excel üzerinde personeli kolayca tanımanız içindir.</td>
                            </tr>
                            <tr>
                                <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">5. İzin Türü / 6. Hakediş Yılı</td>
                                <td className="py-2 px-3"><span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Opsiyonel</span></td>
                                <td className="py-2 px-3">Yıllık Ücretli İzin / 2026</td>
                                <td className="py-2 px-3">Boş bırakılırsa firmanın ana Yıllık İzin türü ve Baz Tarihin yılı seçilir.</td>
                            </tr>
                            <tr>
                                <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">7. Kalan İzin (Gün)</td>
                                <td className="py-2 px-3"><span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-bold">Zorunlu</span></td>
                                <td className="py-2 px-3 font-mono font-bold">28 veya 14,5</td>
                                <td className="py-2 px-3">Personelin içeride kalan net izin günüdür. Küsuratlı saat varsa 8. sütuna (Saat) yazılabilir.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 2. ADIM: Aktarım Ayarları ve Dosya Yükleme Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 space-y-5">
                <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                        2. Adım • Baz Tarih Seçimi ve Aktarım
                    </span>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
                        Excel Tablosunu Yükleyin ve Bakiyeleri Aktarın
                    </h2>
                </div>

                {/* Firma Kilidi / Aktif Yükleme Uyarısı */}
                {uploading && progress?.isRunning && (
                    <div className="rounded-xl bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-4 flex items-center gap-3">
                        <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <div className="text-sm text-blue-900 dark:text-blue-200">
                            <strong>Firma Aktarım Kilidi Aktif:</strong> Şu anda{' '}
                            <span className="underline font-bold">{progress.startedByUsername || 'bir kullanıcı'}</span>{' '}
                            tarafından başlatılan izin bakiye aktarımı devam etmektedir. Veri bütünlüğünü korumak adına işlem tamamlanana kadar bu firmadaki hiçbir kullanıcı yeni bir bakiye yüklemesi başlatamaz.
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Sol: Excel Baz Tarihi + Otomatik Hakediş Eşitleme */}
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 space-y-3">
                        <label className="block text-sm font-bold text-gray-900 dark:text-white">
                            📅 Excel Verisinin Hazırlandığı Baz Tarih (Kesim Tarihi) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={excelBaseDate}
                            disabled={uploading}
                            onChange={(e) => setExcelBaseDate(e.target.value)}
                            className="w-full px-3.5 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-semibold focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Excel'deki bakiye rakamları hangi günün güncel durumunu yansıtıyorsa (ör. tabloyu eski sistemden 5 gün önce çektiyseniz 5 gün önceki tarihi) seçiniz.
                        </p>

                        <label className="flex items-start gap-2.5 pt-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={syncAutoAnnualStartDate}
                                disabled={uploading}
                                onChange={(e) => setSyncAutoAnnualStartDate(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                                <strong>Otomatik Yıllık İzin Hakediş Başlangıç Tarihini</strong> yukarıda seçtiğim Excel Baz Tarihine otomatik eşitle <span className="text-emerald-600 dark:text-emerald-400 font-bold">(Kesinlikle Önerilir)</span>
                            </span>
                        </label>
                    </div>

                    {/* Sağ: Aktarım Modu */}
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 space-y-3">
                        <label className="block text-sm font-bold text-gray-900 dark:text-white">
                            ⚙️ Bakiye Aktarım Modu
                        </label>

                        <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            importMode === 'Equalize'
                                ? 'bg-blue-50/80 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                            <input
                                type="radio"
                                name="importMode"
                                value="Equalize"
                                disabled={uploading}
                                checked={importMode === 'Equalize'}
                                onChange={() => setImportMode('Equalize')}
                                className="mt-1 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="text-xs">
                                <div className="font-bold text-gray-900 dark:text-white">
                                    🔄 Bakiyeyi Excel'deki Değere Eşitle (Önerilen — Devir Modu)
                                </div>
                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                    Personelin kalan iznini Excel'deki net güne eşitler. Aynı Excel dosyasını düzeltip tekrar yükleseniz dahi bakiyeler ikiye katlanmaz.
                                </div>
                            </div>
                        </label>

                        <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            importMode === 'Add'
                                ? 'bg-blue-50/80 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}>
                            <input
                                type="radio"
                                name="importMode"
                                value="Add"
                                disabled={uploading}
                                checked={importMode === 'Add'}
                                onChange={() => setImportMode('Add')}
                                className="mt-1 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="text-xs">
                                <div className="font-bold text-gray-900 dark:text-white">
                                    ➕ Mevcut Bakiyenin Üzerine Ekle (+İlave Modu)
                                </div>
                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                    Personelin mevcut bakiyesine dokunmadan Excel'deki gün/saat miktarını doğrudan üzerine ekler.
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Dosya Seçim Alanı */}
                <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                    uploading
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 opacity-60 pointer-events-none'
                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
                }`}>
                    <input
                        type="file"
                        id="leave-balance-file-upload"
                        accept=".xlsx, .xls"
                        disabled={uploading}
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <label
                        htmlFor="leave-balance-file-upload"
                        className="cursor-pointer flex flex-col items-center justify-center space-y-2"
                    >
                        <Upload className="w-10 h-10 text-blue-500" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                            {file ? (
                                <span className="text-blue-600 dark:text-blue-400 font-bold">
                                    {file.name} ({formatFileSize(file.size)})
                                </span>
                            ) : (
                                'Excel dosyasını seçmek için tıklayın (.xlsx veya .xls)'
                            )}
                        </span>
                        <span className="text-xs text-gray-400">
                            Yüklenen her dosya sunucuda firmanıza özel klasörde tarih, saat ve kullanıcı bilgisiyle otomatik arşivlenir.
                        </span>
                    </label>
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className={`inline-flex items-center px-6 py-3 rounded-xl font-bold text-white shadow-md transition-all ${
                            !file || uploading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Aktarım Devam Ediyor…
                            </>
                        ) : (
                            <>
                                <ShieldCheck className="w-5 h-5 mr-2" />
                                İzin Bakiyelerini Aktar ve Arşivle
                            </>
                        )}
                    </button>
                </div>

                {/* Canlı İlerleme Çubuğu (Progress Bar) */}
                {progress && (uploading || progress.isRunning) && (
                    <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center text-blue-800 dark:text-blue-200 font-bold">
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {progress.message || 'İzin bakiyeleri işleniyor…'}
                            </span>
                            <span className="text-blue-900 dark:text-blue-100 font-extrabold text-base">
                                %{progress.progressPercent ?? 0}
                            </span>
                        </div>
                        <div className="w-full bg-blue-100 dark:bg-blue-950 rounded-full h-3.5 overflow-hidden">
                            <div
                                className="bg-blue-600 h-3.5 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(100, Math.max(2, progress.progressPercent ?? 0))}%` }}
                            />
                        </div>
                        <div className="flex flex-wrap justify-between text-xs text-blue-700 dark:text-blue-300 gap-2 font-medium">
                            <span>
                                İşlenen Satır: <strong>{progress.processedRows ?? 0}</strong> / <strong>{progress.totalRows ?? 0}</strong>
                            </span>
                            <span>
                                Başarılı: <strong className="text-emerald-700 dark:text-emerald-400">{progress.successCount ?? 0}</strong> •{' '}
                                Hatalı: <strong className="text-red-600 dark:text-red-400">{progress.failedCount ?? 0}</strong>
                            </span>
                            {progress.etaSeconds != null && progress.etaSeconds > 0 && (
                                <span>Tahmini Kalan Süre: ~{progress.etaSeconds} sn</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Hata Bildirimi */}
                {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 rounded-xl flex items-center">
                        <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                )}

                {/* Tamamlanma Özeti */}
                {result && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center">
                                <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mr-3 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">Başarılı Aktarılan Personel</div>
                                    <div className="text-2xl font-extrabold text-emerald-800 dark:text-emerald-200">{result.successCount}</div>
                                </div>
                            </div>

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl flex items-center">
                                <Info className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">Zaten Eşit Olan (Değişmeyen)</div>
                                    <div className="text-2xl font-extrabold text-blue-800 dark:text-blue-200">{result.skippedZeroDeltaCount}</div>
                                </div>
                            </div>

                            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl flex items-center">
                                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
                                <div>
                                    <div className="text-xs text-red-700 dark:text-red-300 font-medium">Hatalı / Eşleşmeyen Satır</div>
                                    <div className="text-2xl font-extrabold text-red-800 dark:text-red-200">{result.failedCount}</div>
                                </div>
                            </div>
                        </div>

                        {result.errors && result.errors.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
                                <div className="bg-red-50 dark:bg-red-900/40 px-4 py-2.5 border-b border-red-200 dark:border-red-800 font-bold text-sm text-red-800 dark:text-red-200">
                                    Satır Bazlı Hata Detayları ({result.errors.length})
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
                                    {result.errors.map((err, idx) => (
                                        <div key={idx} className="p-3 text-xs text-gray-700 dark:text-gray-300 flex items-start">
                                            <span className="text-red-500 mr-2 font-bold">•</span>
                                            {err}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 3. ADIM: Sunucu Excel Arşivi (Geçmiş Yüklemeler) */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                            Sunucu Dosya Arşivi &amp; Denetim İzi
                        </span>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
                            Geçmiş İzin Bakiye Aktarımları
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={loadSettingsAndHistory}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingHistory ? 'animate-spin' : ''}`} />
                        Yenile
                    </button>
                </div>

                {history.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                        Henüz bu firma için arşivlenmiş bir İzin Bakiye Aktarım Excel dosyası bulunmuyor.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 uppercase">
                                <tr>
                                    <th className="py-2.5 px-3">Yüklenme Tarihi</th>
                                    <th className="py-2.5 px-3">Excel Baz Tarihi</th>
                                    <th className="py-2.5 px-3">Yükleyen Kullanıcı</th>
                                    <th className="py-2.5 px-3">Aktarım Modu</th>
                                    <th className="py-2.5 px-3">Başarılı / Hatalı</th>
                                    <th className="py-2.5 px-3">Arşiv Dosya Adı</th>
                                    <th className="py-2.5 px-3 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
                                {history.map((item, idx) => (
                                    <tr key={item.storedFileName || idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40">
                                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                                            {formatDateTimeTr(item.uploadedAt)}
                                        </td>
                                        <td className="py-2.5 px-3 whitespace-nowrap">
                                            <span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">
                                                {formatDateTr(item.excelBaseDate)}
                                            </span>
                                        </td>
                                        <td className="py-2.5 px-3 font-medium">
                                            {item.uploadedBy || '-'}
                                        </td>
                                        <td className="py-2.5 px-3">
                                            {item.importMode === 'Add' ? (
                                                <span className="px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-semibold">
                                                    Üzerine Ekleme (+)
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-semibold">
                                                    Bakiye Eşitleme (Devir)
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-3 whitespace-nowrap">
                                            <span className="text-emerald-600 dark:text-emerald-400 font-bold">{item.successCount}</span> başarılı /{' '}
                                            <span className="text-red-600 dark:text-red-400 font-bold">{item.failedCount}</span> hatalı
                                        </td>
                                        <td className="py-2.5 px-3 font-mono text-[11px] text-gray-500 max-w-xs truncate" title={item.storedFileName}>
                                            {item.storedFileName} ({formatFileSize(item.fileSizeBytes)})
                                        </td>
                                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadArchivedFile(item.storedFileName)}
                                                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold transition-colors"
                                            >
                                                <Download className="w-3.5 h-3.5 mr-1" />
                                                İndir
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
